import { toast } from "@carbon6/c6-frontend-design";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import {
  readCatalogFilters,
  readPolicySelectionIds,
  toEvalRunScope,
  type PolicySelectionReader,
  type SessionScope,
} from "@/modules/training/public";
import { isSucceededEvalStatus } from "@/modules/evaluation/public";
import {
  clearLegacyResultsParams,
  hasLegacyResultsParams,
  isLegacyResultsEntry,
  parseEvaluationView,
} from "./evaluationResultsUrl";
import { loadEvalRunSummaries } from "./evalView";
import { openEvaluationResultsWindow } from "./evaluationResultsWindow";

/**
 * 결과 뷰를 탭과 URL 로 표현하던 구조의 딥링크를 처리한다. 결과 화면을 가리키면(`?tab=results`,
 * `view=`/`eval_run=`) Evaluation Results 창을 자동으로 연다. 어느 경우든 이전 키를 URL 에서
 * 정리한다. 이전 키는 이 1회 읽기에서만 쓴다. 현재 URL 표현(활성 창의 정책 조합을 반영하는
 * `?results_policy`)은 `useResultsWindowUrlSync` 가 소유한다.
 *
 * 대기 조건:
 * - team 스코프는 그룹 해석을 기다린 뒤에 연다.
 * - `?policy=` 가 있는 딥링크는 선택 hydration(정책 이름까지 확보)을 기다린 뒤에 연다.
 * `view=` 딥링크는 summary 를 한 번에 모아 받는다(§7). 복원할 수 없으면 안내한 뒤 뷰 없이 창만 연다.
 */
export function useLegacyResultsDeepLink({
  scope,
  groupUuid,
  groupsResolving,
  policySource,
  selectionHydrated,
}: {
  scope: SessionScope;
  groupUuid: string | null;
  groupsResolving: boolean;
  policySource: PolicySelectionReader;
  selectionHydrated: boolean;
}): void {
  const [searchParams, setSearchParams] = useSearchParams();
  // 한 번만 처리한다. StrictMode 이중 effect 와 정리 커밋 전의 재렌더가 두 번째 창이나 두 번째
  // 정리를 만들지 않게 막는다. AnnotationSetDeepLinkPage 와 같은 방식이다.
  const openedRef = useRef(false);

  useEffect(() => {
    if (openedRef.current || !hasLegacyResultsParams(searchParams)) {
      return;
    }
    const opensResults = isLegacyResultsEntry(searchParams);
    // 창을 열어야 하는 진입만 데이터 준비를 기다린다. 정리 전용 키는 즉시 지운다.
    if (opensResults) {
      if (scope === "team" && groupsResolving) {
        return;
      }
      if (readPolicySelectionIds(searchParams).length > 0 && !selectionHydrated) {
        return;
      }
    }
    openedRef.current = true;
    const cleanup = () => {
      setSearchParams((prev) => clearLegacyResultsParams(prev), { replace: true });
    };
    if (!opensResults) {
      cleanup();
      return;
    }
    // 그룹이 없는 team 스코프는 조회할 데이터가 없다. 정리만 하고 창은 열지 않는다.
    const runScope = toEvalRunScope(scope, groupUuid);
    if (runScope === null) {
      cleanup();
      return;
    }
    const policies = policySource.getSelectedPolicies();
    const catalogFilters = readCatalogFilters(searchParams, scope);
    const openWindow = (
      initialView?: Parameters<typeof openEvaluationResultsWindow>[0]["initialView"],
    ) => openEvaluationResultsWindow({ scope: runScope, policies, catalogFilters, initialView });

    const parsed = parseEvaluationView(searchParams);
    if (!parsed.valid) {
      toast.error("Evaluation 보기 URL 이 올바르지 않습니다.");
      openWindow();
      cleanup();
      return;
    }
    const state = parsed.state;
    if (state === null) {
      openWindow();
      cleanup();
      return;
    }
    void loadEvalRunSummaries(state.evalRunIds)
      .then((runs) => {
        // 표의 선택 가드(canSelect)와 같은 계약이다. 비교·병합은 끝난 run 만 다룬다.
        if (state.mode !== "detail" && runs.some((run) => !isSucceededEvalStatus(run.status))) {
          throw new Error("비교·통합 보기는 성공한 Evaluation 실행만 열 수 있습니다.");
        }
        openWindow({ mode: state.mode, runs });
      })
      .catch(() => {
        toast.error("Evaluation 보기를 복원하지 못했습니다.");
        openWindow();
      })
      .finally(cleanup);
  }, [
    searchParams,
    setSearchParams,
    scope,
    groupUuid,
    groupsResolving,
    policySource,
    selectionHydrated,
  ]);
}
